// src/features/payments/components/overview/PaymentsOverview.tsx
import React, { useMemo, useState } from 'react';
import type { Contract } from 'src/types/contract.type';
import type { PaymentsOverviewDto } from 'src/features/payments/hooks/usePaymentsOverview';
import CashflowChart, { type MonthlyRow as ChartMonthlyRow} from './CashflowChart';

function fmtMoney(amount: number, currency?: string | null) {
    const n = Number.isFinite(amount) ? amount : 0;
    const code = (currency ?? '').toUpperCase();
    if (!code) return `${Math.round(n).toLocaleString()}`;
    const locale = code === 'KRW' ? 'ko-KR' : 'en-US';
    try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(n);
    } catch {
        return `${Math.round(n).toLocaleString()} ${code}`;
    }
}

function pct(part: number, total: number) {
    if (!total || total <= 0) return null;
    return (part / total) * 100;
}

function thisMonthKey() {
    const d = new Date();
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    return `${y}-${String(m).padStart(2, '0')}`;
}

function MiniStats({
    currency,
    contracts,
    totals,
}: {
    currency: string | null;
    contracts: PaymentsOverviewDto['contracts'];
    totals: PaymentsOverviewDto['totals'];
}) {
    const netPct = useMemo(() => {
        const base = totals.clientPlanTotal + totals.subconPlanTotal;
        return base > 0 ? pct(totals.netActualTotal, base) : null;
    }, [totals]);

    return (
        <>
            {/* desktop: 헤더 오른쪽 */}
            <div className="hidden sm:flex items-center gap-3 text-[11px] text-gray-600">
                <span className="px-2 py-1 rounded-full border bg-white">
                    Currency: <b className="text-gray-800">{currency ?? '—'}</b>
                </span>
                <span className="px-2 py-1 rounded-full border bg-white">
                    Contracts: <b className="text-gray-800">{contracts.total}</b>
                    <span className="text-gray-400">
                        {' '}
                        (C {contracts.clientCount} / S {contracts.subconCount})
                    </span>
                </span>
                <span className="px-2 py-1 rounded-full border bg-white">
                    Net(Actual): <b className="text-gray-800">{fmtMoney(totals.netActualTotal, currency)}</b>
                    {typeof netPct === 'number' && <span className="text-gray-400"> ({netPct.toFixed(0)}%)</span>}
                </span>
            </div>

            {/* mobile: 아래로 */}
            <div className="sm:hidden mt-2 overflow-x-auto">
                <div className="flex gap-2 text-[11px] text-gray-600 w-max pb-1">
                    <span className="px-2 py-1 rounded-full border bg-white">
                        Currency: <b className="text-gray-800">{currency ?? '—'}</b>
                    </span>
                    <span className="px-2 py-1 rounded-full border bg-white">
                        Contracts: <b className="text-gray-800">{contracts.total}</b>
                        <span className="text-gray-400">
                            {' '}
                            (C {contracts.clientCount} / S {contracts.subconCount})
                        </span>
                    </span>
                    <span className="px-2 py-1 rounded-full border bg-white">
                        Net(Actual): <b className="text-gray-800">{fmtMoney(totals.netActualTotal, currency)}</b>
                    </span>
                </div>
            </div>
        </>
    );
}

function Card({ title, value, sub }: { title: string; value: string; sub?: string }) {
    return (
        <div className="rounded-2xl border bg-white p-4">
            <div className="text-xs text-gray-500">{title}</div>
            <div className="text-xl font-semibold mt-1">{value}</div>
            {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function PaymentsOverview({
    projectId,
    currency,
    contracts,
    overview,
}: {
    projectId: string;
    currency: string | null;
    contracts: Contract[];
    overview: PaymentsOverviewDto | null;
}) {
    const [collapsed, setCollapsed] = useState(false);

    const safe = overview ?? {
        currency,
        contracts: {
            total: contracts.length,
            clientCount: contracts.filter((c) => c.direction === 'CLIENT').length,
            subconCount: contracts.filter((c) => c.direction === 'SUBCON').length,
        },
        totals: {
            clientPlanTotal: 0,
            clientActualTotal: 0,
            subconPlanTotal: 0,
            subconActualTotal: 0,
            netPlanTotal: 0,
            netActualTotal: 0,
        },
        monthly: [],
    };

    const monthKey = thisMonthKey();
    const thisMonth = safe.monthly.find((r) => r.month === monthKey) ?? null;

    // ✅ 차트가 기대하는 Vendor 필드명으로 매핑
    const chartData: ChartMonthlyRow[] = useMemo(() => {
        return (safe.monthly ?? []).map((r) => ({
            month: r.month,
            planClient: r.planClient,
            actualClient: r.actualClient,
            planVendor: (r as any).planSubcon ?? 0, // 혹시 필드명이 다르면 방어
            actualVendor: (r as any).actualSubcon ?? 0,
            cumClient: r.cumClient,
            cumVendor: (r as any).cumSubcon ?? 0,
            netCum: r.netCum,
        }));
    }, [safe.monthly]);

    return (
        <div className="w-full mb-4 rounded-2xl border bg-white p-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                    <button
                        type="button"
                        aria-label={collapsed ? 'Expand overview' : 'Collapse overview'}
                        aria-expanded={!collapsed}
                        onClick={() => setCollapsed((v) => !v)}
                        className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 transition"
                        title={collapsed ? '펼치기' : '접기'}
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            className={`transition-transform ${collapsed ? '' : 'rotate-90'}`}
                        >
                            <path d="M8 5l8 7-8 7V5z" fill="currentColor" />
                        </svg>
                    </button>
                    <h3 className="font-medium">Payments Overview</h3>
                </div>

                <MiniStats currency={safe.currency} contracts={safe.contracts} totals={safe.totals} />
            </div>

            {!collapsed && (
                <>
                    {/* ✅ Cards: PC 1줄 / 태블릿 2줄 / 모바일 1줄(가로스크롤) */}
                    <div
                        className={[
                            'mt-3 gap-3',
                            'grid grid-flow-col auto-cols-[minmax(220px,1fr)] overflow-x-auto',
                            'md:grid-flow-row md:auto-cols-auto md:grid-cols-2 md:overflow-visible',
                            'lg:grid-cols-4',
                        ].join(' ')}
                    >
                        <Card
                            title="Cashflow (Net)"
                            value={fmtMoney(safe.totals.netActualTotal, safe.currency)}
                            sub={`Plan ${fmtMoney(safe.totals.netPlanTotal, safe.currency)} · Actual ${fmtMoney(
                                safe.totals.netActualTotal,
                                safe.currency,
                            )}`}
                        />

                        <Card
                            title="Client Received"
                            value={fmtMoney(safe.totals.clientActualTotal, safe.currency)}
                            sub={`Plan ${fmtMoney(safe.totals.clientPlanTotal, safe.currency)} · Actual ${fmtMoney(
                                safe.totals.clientActualTotal,
                                safe.currency,
                            )}`}
                        />

                        <Card
                            title="Subcon Paid"
                            value={fmtMoney(safe.totals.subconActualTotal, safe.currency)}
                            sub={`Plan ${fmtMoney(safe.totals.subconPlanTotal, safe.currency)} · Actual ${fmtMoney(
                                safe.totals.subconActualTotal,
                                safe.currency,
                            )}`}
                        />

                        <Card
                            title="This Month Net"
                            value={thisMonth ? fmtMoney(thisMonth.actualNet, safe.currency) : '—'}
                            sub={
                                thisMonth
                                    ? `Plan ${fmtMoney(thisMonth.planNet, safe.currency)} · Actual ${fmtMoney(
                                          thisMonth.actualNet,
                                          safe.currency,
                                      )}`
                                    : 'No monthly data'
                            }
                        />
                    </div>

                    {/* ✅ Chart */}
                    <div className="mt-3">
                        <CashflowChart data={chartData} />
                    </div>
                </>
            )}
        </div>
    );
}
